import React from 'react';
import { BRAND, COMPLIANCE_FOOTER } from '../../data/mock';
import { Button } from '../ui/button';
import { PawPrint, MapPin, Phone, Mail, Youtube, ArrowRight } from 'lucide-react';

export const Footer = ({ onContactClick }) => {
  const footerLinks = {
    quickLinks: [
      { label: 'How It Works', href: '#how-it-works' },
      { label: 'Why Us', href: '#why-different' },
      { label: 'Coverage Options', href: '#customization' },
      { label: 'States We Serve', href: '#states' },
      { label: 'About Us', href: '#about' },
    ],
    states: [
      { label: 'Florida Pet Insurance', href: '#states' },
      { label: 'North Carolina Pet Insurance', href: '#states' },
      { label: 'Virginia Pet Insurance', href: '#states' },
    ]
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-slate-900 text-white">
      {/* Main CTA Banner */}
      <div className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold mb-2">
                Ready to protect your pet?
              </h3>
              <p className="text-slate-400">
                Get your free quote in minutes — no obligation required.
              </p>
            </div>
            <Button
              asChild
              size="lg"
              className="text-white font-bold text-lg px-8 py-6 shadow-xl shadow-orange-500/30 hover:scale-105 transition-all"
              style={{ background: 'linear-gradient(135deg, #f97316 0%, #fb7185 100%)' }}
            >
              <a href={BRAND.quoteUrl} target="_blank" rel="noopener noreferrer">
                Get Your Free Quote Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <a href="/" className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                <PawPrint className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold">Local Pet Advisors</span>
            </a>
            <p className="text-slate-400 mb-6">
              Licensed pet insurance expert serving pet parents across Florida, North Carolina, and Virginia.
            </p>
            <div className="flex items-center gap-4">
              <a
                href={BRAND.youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 hover:bg-red-500 flex items-center justify-center transition-colors"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {footerLinks.quickLinks.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-slate-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* States */}
          <div>
            <h4 className="font-semibold text-lg mb-6">States We Serve</h4>
            <ul className="space-y-3">
              {footerLinks.states.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-slate-400 hover:text-white transition-colors flex items-center gap-2"
                  >
                    <MapPin className="w-4 h-4" />
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${BRAND.phone}`}
                  className="text-slate-400 hover:text-white transition-colors flex items-center gap-3"
                >
                  <Phone className="w-5 h-5 text-teal-500" />
                  Talk to a Local Advisor
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${BRAND.email}`}
                  className="text-slate-400 hover:text-white transition-colors flex items-center gap-3"
                >
                  <Mail className="w-5 h-5 text-teal-500" />
                  {BRAND.email}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Compliance Footer */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-sm text-slate-500 text-center leading-relaxed">
            {COMPLIANCE_FOOTER.text}
          </p>
          <p className="text-sm text-slate-600 text-center mt-4">
            © {new Date().getFullYear()} {BRAND.name}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
